import { enqueue, flush, flushBeacon } from "./client";
import { getAnonymousId } from "./identity";
import { captureLaunchUtm } from "./utm";
import { captureLandingPage } from "./attribution";
import { captureLaunchSource } from "./source";
import { initElementTracking } from "./element-observer";

let initialized = false;

/**
 * Bootstrap the analytics layer. Idempotent — safe to call from a remounting
 * provider / React StrictMode. Must run once per cold launch, as early as
 * possible, because it re-derives the launch-scoped UTM.
 */
export const initAnalytics = (): void => {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  // Order matters: anon id first, then re-derive this launch's UTM and landing
  // page, then open the session (which emits `session_started`).
  getAnonymousId();
  captureLaunchSource();
  captureLaunchUtm();
  captureLandingPage();
  enqueue("session_started");

  // Click/view instrumentation is DOM-level and independent of the funnel
  // events — it no-ops when VITE_ELEMENT_TRACKING_ENABLED is off.
  initElementTracking();

  const onHide = () => {
    if (document.visibilityState === "hidden") flushBeacon();
  };
  document.addEventListener("visibilitychange", onHide);
  window.addEventListener("pagehide", flushBeacon);
  // Drain anything that piled up while offline.
  window.addEventListener("online", () => void flush());
};

export { enqueue, flush, flushBeacon };
export * from "./events";
export {
  getSignupAttribution,
  getBuyAttribution,
  getLandingPage,
} from "./attribution";
export type { SignupAttribution } from "./attribution";
export { getActiveUtm, getFirstTouchUtm } from "./utm";
export { getSource } from "./source";
export {
  trackElementClicked,
  trackElementViewed,
  ELEMENT_TYPES,
  TARGET_TYPES,
} from "./element-tracking";
export type { ElementDescriptor, TargetType } from "./element-tracking";
export { resolvePageType, currentPageType } from "./page-type";
export type { UtmParams, ClientEventName } from "./types";
